class CategorieNav {
  constructor(element, options = {}) {
    this.element = element;

    if (!this.element) {
      return;
    }

    this.options = { ...CategorieNav.defaultOptions, ...options };

    this.categriesMenu = this.element.querySelector(
      '[data-element="categories-nav-menu"]',
    );
    this.categriesMenuItems = this.categriesMenu?.querySelectorAll(
      '[data-element="categories-nav-item"]',
    ) || [];
    this.contentSection = this.element.querySelector(
      '[data-element="categories-nav-content"]',
    );
    this.btnBackToNavSection = this.element.querySelector(
      '[data-element="btn-categories-back"]',
    );

    this.init();
  }

  static defaultOptions = {
    scrollMarginTop: 120,
  };

  init = () => {
    this.setScrollMargin();
    this.initEventListeners();

    // Handle initial hash on page load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(() => this.handleInitialHash(), 100);
      });
    } else {
      setTimeout(() => this.handleInitialHash(), 100);
    }
  };

  initEventListeners = () => {
    this.categriesMenuItems.forEach((item) => {
      item.addEventListener("click", (event) => {
        this.handleCategoryClick(event, item);
      });
    });

    if (this.btnBackToNavSection) {
      this.btnBackToNavSection.addEventListener(
        "click",
        this.handleBackToNavSectionClick,
      );
    }

    // Handle browser navigation (back/forward buttons)
    window.addEventListener('popstate', (event) => {
      if (event.state && event.state.categoryId) {
        this.showCategoryContent(event.state.categoryId, event.state.itemId);
      } else {
        this.resetToDefaultState();
      }
    });
  };

  handleCategoryClick = (event, element) => {
    event.preventDefault();

    const hrefParts = element.getAttribute("href").split("#")[1];

    this.updateUrl(hrefParts);
    this.showCategoryContent(hrefParts);
  };

  handleBackToNavSectionClick = () => {
    window.scrollTo({
      top: Math.max(0, this.categriesMenu.offsetTop - this.options.scrollMarginTop - 20),
      behavior: "smooth",
    });
  };

  /**
   * Optional: Handles clicking on individual items within categories
   * Call initItemEventListeners() to enable this functionality
   * @param {Event} event - The click event
   * @param {HTMLElement} element - The clicked item element
   */
  handleItemClick = (event, element) => {
    if (event.target.tagName === 'A') {
      event.preventDefault();
    }

    const itemId = element.id;
    const parentCategory = element.closest('[data-element="categories-nav-content-item"]');

    if (parentCategory) {
      const categoryId = parentCategory.id;
      this.updateUrl(categoryId, itemId);
      this.showCategoryContent(categoryId, itemId);
    }
  };

  setScrollMargin() {
    if (!this.contentSection) {
      return;
    }

    const scrollMarginTop = this.options.scrollMarginTop;
    this.contentSection.style.scrollMarginTop = `${scrollMarginTop}px`;
  }

  /**
   * Handles initial page load with hash in URL
   */
  handleInitialHash = () => {
    const hash = window.location.hash.substring(1); // Remove the '#'

    if (!this.checkDomElements()) {
      return;
    }

    if (hash) {
      const hashParts = hash.split('#').filter(part => part.length > 0);

      if (hashParts.length === 2) {
        const [categoryId, itemId] = hashParts;
        this.showCategoryContent(categoryId, itemId);
      } else if (hashParts.length === 1) {
        const singleId = hashParts[0];
        const categoryContent = document.getElementById(singleId);

        if (categoryContent && categoryContent.hasAttribute('data-element') &&
          categoryContent.getAttribute('data-element') === 'categories-nav-content-item') {
          this.showCategoryContent(singleId);
        } else {
          this.showItemContent(singleId);
        }
      }
    }
  };

  /**
   * Shows category content by ID and updates navigation state
   * @param {string} categoryId - The ID of the category content to show
   * @param {string} [itemId] - Optional item ID to scroll to within the category
   */
  showCategoryContent = (categoryId, itemId = null) => {
    // Reset all menu items
    this.categriesMenuItems.forEach((item) => {
      item.setAttribute("aria-selected", "false");
    });

    // Activate corresponding menu item
    const menuItem = Array.from(this.categriesMenuItems).find((item) => {
      const href = item.getAttribute("href");
      return href && href.split("#")[1] === categoryId;
    });

    if (menuItem) {
      menuItem.setAttribute("aria-selected", "true");
    }

    // Show content section
    this.contentSection.classList.remove("hidden");
    this.contentSection.classList.add("block");

    // Reset all content items
    this.contentSection
      .querySelectorAll('[data-element="categories-nav-content-item"]')
      .forEach((content) => {
        content.setAttribute("aria-expanded", "false");
      });

    // Show specific content
    const content = document.getElementById(categoryId);

    if (content) {
      content.setAttribute("aria-expanded", "true");

      setTimeout(() => {
        if (itemId) {
          this.scrollToItem(categoryId, itemId);
        } else {
          this.performScroll(content.offsetTop);
        }
      }, 100);
    }
  };

  /**
   * Shows content for a specific item by finding its parent category
   * @param {string} itemId - The ID of the item to show
   */
  showItemContent = (itemId) => {
    const itemElement = document.getElementById(itemId);

    if (!itemElement) {
      return;
    }

    const parentCategory = itemElement.closest('[data-element="categories-nav-content-item"]');

    if (parentCategory) {
      const categoryId = parentCategory.id;
      this.showCategoryContent(categoryId, itemId);
    }
  };

  /**
   * Scrolls to a specific item within a category
   * @param {string} categoryId - The category ID
   * @param {string} itemId - The item ID to scroll to
   */
  scrollToItem = (categoryId, itemId) => {
    const itemElement = document.getElementById(itemId);

    if (itemElement) {
      this.performScroll(itemElement.offsetTop);
    } else {
      // Fallback: scroll to category if item not found
      const categoryElement = document.getElementById(categoryId);
      if (categoryElement) {
        this.performScroll(categoryElement.offsetTop);
      }
    }
  };

  // Helper method for scrolling logic
  performScroll = (elementTop) => {
    const targetOffset = Math.max(0, elementTop - this.options.scrollMarginTop);
    const currentScroll = window.pageYOffset || document.documentElement.scrollTop;

    if (Math.abs(targetOffset - currentScroll) >= 50) {
      window.scrollTo({
        top: targetOffset,
        behavior: "smooth",
      });
    }
  };

  /**
   * Updates the browser URL with category and/or item hash
   * @param {string} categoryId - The category ID to add to URL
   * @param {string} [itemId] - Optional item ID to add to URL
   */
  updateUrl = (categoryId, itemId = null) => {
    const hash = itemId ? `${categoryId}#${itemId}` : categoryId;
    const newUrl = `${window.location.pathname}${window.location.search}#${hash}`;

    if (window.history.pushState) {
      window.history.pushState({ categoryId, itemId }, '', newUrl);
    } else {
      window.location.hash = hash;
    }
  };

  /**
   * Resets the navigation to its default state (no category selected)
   */
  resetToDefaultState = () => {
    // Reset all menu items
    this.categriesMenuItems.forEach((item) => {
      item.setAttribute("aria-selected", "false");
    });

    // Hide content section
    this.contentSection.classList.remove("block");
    this.contentSection.classList.add("hidden");

    // Reset all content items
    this.contentSection
      .querySelectorAll('[data-element="categories-nav-content-item"]')
      .forEach((content) => {
        content.setAttribute("aria-expanded", "false");
      });

    // Scroll to menu
    window.scrollTo({
      top: Math.max(0, this.categriesMenu.offsetTop - this.options.scrollMarginTop - 20),
      behavior: "smooth",
    });
  };

  /**
   * Optional: Initialize click handlers for individual items
   * Call this method if you want items to be clickable and update URLs
   * @param {string} [itemSelector] - CSS selector for clickable items (default: '[id]')
   */
  initItemEventListeners = (itemSelector = '[id]') => {
    const items = this.contentSection.querySelectorAll(`${itemSelector}`);

    items.forEach((item) => {
      if (item.id && item.closest('[data-element="categories-nav-content-item"]')) {
        item.addEventListener('click', (event) => {
          this.handleItemClick(event, item);
        });
        item.style.cursor = 'pointer';
      }
    });
  };

  /**
   * Optional: Navigate to a specific category and item programmatically
   * @param {string} categoryId - The category ID
   * @param {string} itemId - The item ID within the category
   * @param {boolean} updateUrl - Whether to update the browser URL (default: true)
   */
  navigateToItem = (categoryId, itemId, updateUrl = true) => {
    if (updateUrl) {
      this.updateUrl(categoryId, itemId);
    }
    this.showCategoryContent(categoryId, itemId);
  };

  /**
   * Check if all required DOM elements are present
   * @returns {boolean} True if all elements are found
   */
  checkDomElements = () => {
    const checks = {
      element: !!this.element,
      menu: !!this.categriesMenu,
      menuItems: this.categriesMenuItems.length > 0,
      contentSection: !!this.contentSection,
      backButton: !!this.btnBackToNavSection
    };

    const allPresent = Object.values(checks).every(check => check);

    return allPresent;
  };
}

export default CategorieNav;
